import { useCallback, useEffect, useState } from 'react';
import { useLoaderData, useRouter, useSearch } from '@tanstack/react-router';
import {
  ArrowLeft,
  Loader2,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Zap,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { overlay } from 'overlay-kit';

import {
  AudioPlayerDuration,
  AudioPlayerProgress,
  AudioPlayerProvider,
  AudioPlayerTime,
} from '@/shared/ui/audio-player';
import { PlayerControlButton } from '@/shared/ui/player-control-button';
import { cn } from '@/shared/utils';
import { usePlyerAudioPlayer } from '@/shared/utils/use-plyer-audio-player';
import { Button } from '@/shared/ui/button';
import { BingeLearning } from './binge-learning';
import { useToast } from '@/shared/ui/toast-provider';
import { usersQueries } from '@/entities/user';
import {
  courseQueries,
  postUpdateProgress,
  type GetCourseDetailResponse,
} from '@/entities/course';
import { patchUsersMe } from '@/entities/user';
import { useUpdateServerPlaybackMode } from '../model/use-update-server-playback-mode';
import type { ErrorResponse } from '@/shared/types/api';
import BackSecondsImage from '@/shared/assets/images/back-seconds.png';
import { useCaptureAnalyticsEvent } from '@/shared/analytics/events';

const formatHoursLabel = (seconds: number): string | null => {
  if (!seconds) return null;
  const hours = seconds / 3600;
  if (hours < 1) {
    const minutes = Math.round(seconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  const formatted =
    hours % 1 === 0 ? Math.round(hours).toString() : hours.toFixed(1);
  return `${formatted} hour${hours === 1 ? '' : 's'}`;
};

type PlayerRouteLoaderData = {
  course: GetCourseDetailResponse | null;
};

export const PlayerPage = () => {
  const router = useRouter();
  const { courseId, contentId } = useSearch({ from: '/_user/courses/plyer' });
  const { course } = useLoaderData({
    from: '/_user/courses/plyer',
  }) as PlayerRouteLoaderData;

  if (!courseId || !contentId) {
    return (
      <MissingCourseInformation
        onBack={() => router.navigate({ to: '/courses' })}
      />
    );
  }

  const hasContent = course?.contents?.some(
    (courseContent) => courseContent.id === contentId,
  );

  if (course && !hasContent) {
    return (
      <MissingContent onBack={() => router.navigate({ to: '/courses' })} />
    );
  }

  return (
    <AudioPlayerProvider>
      <PlayerPageContent courseId={courseId} contentId={contentId} />
    </AudioPlayerProvider>
  );
};

const MissingCourseInformation = ({ onBack }: { onBack: () => void }) => (
  <main className="flex min-h-screen items-center justify-center bg-white px-4 pb-28 text-slate-900">
    <section className="w-full max-w-sm rounded-3xl border border-slate-200/80 bg-white p-8 text-center shadow-[0_24px_60px_rgba(15,23,42,0.1)]">
      <h1 className="text-xl font-semibold text-slate-900">
        Missing course information
      </h1>
      <p className="mt-3 text-sm text-slate-500">
        The player needs both course and content identifiers. Please return to
        the course list and try again.
      </p>
      <Button
        className="mx-auto mt-6 h-11 w-full rounded-xl text-sm font-semibold"
        onClick={onBack}
      >
        Back to courses
      </Button>
    </section>
  </main>
);

const MissingContent = ({ onBack }: { onBack: () => void }) => (
  <main className="flex min-h-screen items-center justify-center bg-white px-6 pb-28 text-slate-900">
    <section className="w-full max-w-sm rounded-3xl border border-slate-200/80 bg-white p-8 text-center shadow-[0_24px_60px_rgba(15,23,42,0.1)]">
      <h1 className="text-xl font-semibold text-slate-900">
        We could not find that module
      </h1>
      <p className="mt-3 text-sm text-slate-500">
        The content associated with this course may have been removed or is
        temporarily unavailable.
      </p>
      <Button
        className="mx-auto mt-6 h-11 w-full rounded-xl text-sm font-semibold"
        onClick={onBack}
      >
        Back to courses
      </Button>
    </section>
  </main>
);

const PlayerPageContent = ({
  courseId,
  contentId,
}: {
  courseId: string;
  contentId: string;
}) => {
  const router = useRouter();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { course } = useLoaderData({
    from: '/_user/courses/plyer',
  }) as PlayerRouteLoaderData;
  const { data: user } = useQuery(usersQueries.me());
  const isServerPlaybackModeActive = Boolean(
    user?.is_server_playback_mode_active,
  );
  const [isOpen, setIsOpen] = useState(isServerPlaybackModeActive);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);

  useEffect(() => {
    setIsOpen(isServerPlaybackModeActive);
  }, [isServerPlaybackModeActive]);

  const {
    content,
    courseTitle,
    courseDurationSecs,
    play,
    pause,
    seekBy,
    seekTo,
    toggleMute,
    state,
    goToNextContent,
    goToPreviousContent,
  } = usePlyerAudioPlayer({
    courseId,
    contentId,
  });

  // Reset auto-play flag when content changes
  useEffect(() => {
    setHasAutoPlayed(false);
  }, [contentId]);

  // Auto-play when player is ready (only once per content)
  useEffect(() => {
    if (state.isReady && !state.isLoading && !hasAutoPlayed) {
      play();
      setHasAutoPlayed(true);
    }
  }, [state.isReady, state.isLoading, hasAutoPlayed, play]);

  const captureAnalyticsEvent = useCaptureAnalyticsEvent();
  const { mutateAsync: updateServerPlaybackMode, isPending: isUpdatingBinge } =
    useUpdateServerPlaybackMode({ courseId });

  const { mutateAsync: ensureInitialProgress, isPending: isEnsuringProgress } =
    useMutation({
      mutationFn: postUpdateProgress,
    });

  const { mutateAsync: syncUserData, isPending: isSyncingUserData } =
    useMutation({
      mutationFn: patchUsersMe,
    });

  const [isActivatingBinge, setIsActivatingBinge] = useState(false);

  const isBingeActionPending =
    isUpdatingBinge || isEnsuringProgress || isActivatingBinge;

  const durationLabel = formatHoursLabel(courseDurationSecs);
  const progressPercent = Math.round(state.progressPercent);

  const currentContentIndex = (() => {
    if (!content || !course?.contents) return undefined;
    const index = course.contents.findIndex(
      (c: { id: string }) => c.id === content.id,
    );
    return index >= 0 ? index + 1 : undefined;
  })();

  const totalContents = course?.contents?.length;

  if (!state.isLoading && !content) {
    return (
      <MissingContent onBack={() => router.navigate({ to: '/courses' })} />
    );
  }

  const title = content?.title || courseTitle || 'Course player';
  const completionLabel = `${progressPercent}% Complete`;

  const handlePlayToggle = () => {
    if (state.isLoading) return;
    if (state.isPlaying) {
      pause();
    } else {
      play();
    }
  };

  const handleEnableBinge = useCallback(async () => {
    if (isServerPlaybackModeActive || isBingeActionPending) return;

    try {
      captureAnalyticsEvent('binge_mode_enable_clicked', {
        course_id: courseId,
        content_id: contentId,
        progress_percent: progressPercent,
        playback_position_secs: state.currentTime,
      });
      setIsActivatingBinge(true);
      const latestUser = await queryClient.fetchQuery(usersQueries.me());

      if (!state.isMuted) {
        toggleMute();
      }

      if (!latestUser?.recent_user_content_association) {
        await ensureInitialProgress({
          content_id: contentId,
          course_id: courseId,
          player_event: 'playing',
          play_position_secs: 0,
          sound_volume: 1,
          playback_speed: 1,
          is_background_playing: true,
        });
      }

      await updateServerPlaybackMode({
        is_server_playback_mode_active: true,
      });
    } catch (error) {
      const err = error as ErrorResponse;
      const message =
        err?.data?.errors?.[0]?.detail ??
        'Please check with your administrator';
      toast({
        type: 'warning',
        title: message,
      });
    } finally {
      setIsActivatingBinge(false);
    }
  }, [
    contentId,
    courseId,
    ensureInitialProgress,
    isBingeActionPending,
    isServerPlaybackModeActive,
    play,
    queryClient,
    progressPercent,
    state.isMuted,
    state.currentTime,
    toast,
    toggleMute,
    updateServerPlaybackMode,
  ]);

  const handleDisableBinge = async () => {
    if (!isServerPlaybackModeActive) return;

    try {
      captureAnalyticsEvent('binge_mode_disable_clicked', {
        course_id: courseId,
        content_id: contentId,
        progress_percent: progressPercent,
        playback_position_secs: state.currentTime,
      });
      pause();
      await new Promise((resolve) => setTimeout(resolve, 300));
      await updateServerPlaybackMode(
        {
          is_server_playback_mode_active: false,
        },
        {
          onSuccess: (data) => {
            seekTo(
              data?.recent_user_content_association?.max_play_position_secs ??
                0,
            );
          },
        },
      );
      setIsOpen(false);
    } catch (error) {
      const err = error as ErrorResponse;
      const message =
        err?.data?.errors?.[0]?.detail ??
        'Please check with your administrator';
      toast({
        type: 'warning',
        title: message,
      });
    }
  };

  const handleSyncProgress = useCallback(async () => {
    if (!isServerPlaybackModeActive) return;

    try {
      captureAnalyticsEvent('binge_mode_sync_clicked', {
        course_id: courseId,
        content_id: contentId,
        progress_percent: progressPercent,
        playback_position_secs: state.currentTime,
      });

      // Sync with server by toggling mode to refresh data
      await syncUserData({
        is_server_playback_mode_active: false,
      });
      const updatedUser = await syncUserData({
        is_server_playback_mode_active: true,
      });

      // Invalidate all related queries to refresh UI
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: usersQueries.me().queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: courseQueries.detail(courseId).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: courseQueries.contentDetail(contentId).queryKey,
        }),
      ]);

      // Update progress if server has newer position
      if (updatedUser?.recent_user_content_association) {
        const serverPosition =
          updatedUser.recent_user_content_association.max_play_position_secs;
        if (serverPosition > state.currentTime) {
          seekTo(serverPosition);
        }
      }

      toast({
        type: 'success',
        title: 'Progress synced successfully',
      });
    } catch (error) {
      const err = error as ErrorResponse;
      const message =
        err?.data?.errors?.[0]?.detail ?? 'Failed to sync progress';
      toast({
        type: 'error',
        title: message,
      });
    }
  }, [
    captureAnalyticsEvent,
    contentId,
    courseId,
    isServerPlaybackModeActive,
    progressPercent,
    queryClient,
    seekTo,
    state.currentTime,
    syncUserData,
    toast,
  ]);

  return (
    <main
      className="flex min-h-screen justify-center bg-white pb-28 text-slate-900"
      data-testid="player-page"
    >
      <div className="flex w-full max-w-md flex-col px-5 pb-12 pt-4">
        <header className="flex items-start justify-between mb-6">
          <Button
            aria-label="Back to courses"
            onClick={() => router.navigate({ to: '/courses' })}
            variant="ghost"
            className="flex items-center justify-center p-0 [&_svg]:size-6 text-slate-500"
          >
            <ArrowLeft />
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={handleEnableBinge}
            disabled={isBingeActionPending || isServerPlaybackModeActive}
            className={cn(
              'flex items-center gap-2 rounded-full border border-indigo-200/60 bg-indigo-50 px-3 py-2 text-indigo-600 shadow-sm transition hover:bg-indigo-100',
              isServerPlaybackModeActive &&
                'border-transparent bg-indigo-600 text-white hover:bg-indigo-600',
            )}
            data-testid="binge-button"
          >
            <span
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full bg-indigo-500 text-white shadow',
                isServerPlaybackModeActive && 'bg-white/20',
              )}
            >
              {isBingeActionPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Zap className="h-4 w-4" />
              )}
            </span>
            <span className="text-xs font-semibold uppercase tracking-wide">
              {isServerPlaybackModeActive ? 'Binge Active' : 'Binge'}
            </span>
          </Button>
        </header>

        <div className="space-y-1">
          <h1 className="text-2xl font-semibold leading-snug text-slate-900">
            {title}
          </h1>
          {durationLabel && (
            <p className="text-sm font-medium text-slate-500">
              {durationLabel}
            </p>
          )}
        </div>

        <section className="mt-10 space-y-4">
          <div
            data-testid="player-progress-completion"
            className="text-center text-sm font-semibold text-slate-600"
          >
            {completionLabel}
          </div>
          <div className="rounded-full bg-slate-100/50 p-4 shadow-inner">
            <AudioPlayerProgress
              className="group cursor-pointer"
              disabled={state.isInteractionDisabled}
              step={0.5}
              onValueChange={(vals) => {
                seekTo(vals[0] ?? 0);
              }}
            />
          </div>
          <div></div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PlayerControlButton
                aria-label={state.isMuted ? 'Unmute' : 'Mute'}
                onClick={toggleMute}
                variant="ghost"
              >
                {state.isMuted ? (
                  <VolumeX className="size-6 text-slate-400" />
                ) : (
                  <Volume2 className="size-6 text-slate-600" />
                )}
              </PlayerControlButton>
              <PlayerControlButton
                aria-label="Rewind 15 seconds"
                onClick={() => seekBy(-15)}
                disabled={state.isInteractionDisabled}
                variant="ghost"
              >
                <img
                  src={BackSecondsImage}
                  alt="Rewind 15 seconds"
                  className="size-6"
                />
              </PlayerControlButton>
            </div>

            {import.meta.env.VITE_PUBLIC_APP_ENV === 'staging' && (
              <div className="flex justify-end text-sm font-medium text-slate-600">
                <AudioPlayerTime className="text-sm font-medium text-slate-600" />{' '}
                /{' '}
                <AudioPlayerDuration className="text-sm font-medium text-slate-600" />
              </div>
            )}
          </div>
        </section>

        <section className="mt-16 space-y-10">
          <div className="flex items-center justify-between">
            <PlayerControlButton
              aria-label="Restart audio"
              onClick={goToPreviousContent}
              disabled={state.isInteractionDisabled}
              variant="ghost"
            >
              <SkipBack className="h-6 w-6" />
            </PlayerControlButton>

            <div
              className={cn(
                'flex-1 flex items-center justify-center',
                import.meta.env.VITE_PUBLIC_APP_ENV === 'prod' &&
                  'right-4 relative',
              )}
            >
              <PlayerControlButton
                aria-label={state.isPlaying ? 'Pause' : 'Play'}
                onClick={handlePlayToggle}
                disabled={state.isInteractionDisabled}
                variant="primary"
                size="lg"
              >
                {state.isLoading ? (
                  <Loader2 className="h-7 w-7 animate-spin" />
                ) : state.isPlaying ? (
                  <Pause className="h-7 w-7" />
                ) : (
                  <Play className="h-7 w-7" />
                )}
              </PlayerControlButton>
            </div>
            {import.meta.env.VITE_PUBLIC_APP_ENV === 'staging' && (
              <PlayerControlButton
                aria-label="Fast-forward 15 seconds"
                onClick={goToNextContent}
                disabled={state.isInteractionDisabled}
                variant="ghost"
              >
                <SkipForward className="h-6 w-6" />
              </PlayerControlButton>
            )}
          </div>
        </section>
      </div>

      {isOpen && (
        <BingeLearning
          open={isOpen}
          title={title}
          progressPercent={progressPercent}
          duration={content?.duration_secs ?? 0}
          isMuted={state.isMuted}
          isPlaying={state.isPlaying}
          currentContentIndex={currentContentIndex}
          totalContents={totalContents}
          onClose={handleDisableBinge}
          onToggleMute={toggleMute}
          onPlay={play}
          onSync={handleSyncProgress}
          isSyncing={isSyncingUserData}
        />
      )}
    </main>
  );
};
